/**
 * ListingsMap - Google Maps with listing markers
 *
 * Diamond Architecture:
 * - Uses the map SEAM (`@/shared/components/map/vis-gl`) for the Google Maps
 *   React primitives — the app never names the third-party package, and the
 *   marker anchor convention that produced this file's popup defect is not
 *   spellable through it (see that module's header).
 * - Markers rendered from proto-generated ListingCard[]
 * - Syncs with ListingsSheet through the SELECTION, one way: a card the
 *   pointer is over reports `onCardHover` and this map highlights its pin.
 *   (The sheet carried a `selectedId` and an `onCardClick` until obs. 4 — that
 *   pair was the two-click card, and the list's ring went with it.)
 * - The selected listing's preview card is a POPOVER: a screen-space rectangle
 *   positioned by Floating UI against the pill's DOM box and bounded by the map
 *   pane (map-popover.ts). It is deliberately NOT an AdvancedMarker — a marker
 *   is a map-PROJECTED point, and no projected point can express "stay inside
 *   this rectangle", which is the entire requirement.
 *
 * - Mounts through `MapBoundary` (D13) with `load="in-view"`: the map loads
 *   when its pane is within a viewport of the screen. On the hub canvas that
 *   is at once, on both breakpoints (the pane is the page's right half on
 *   desktop and the backdrop above the resting results drawer on mobile — it
 *   is never hidden behind a toggle); on an INLINE facet the pane sits under
 *   a heading and can start below the fold, and there it now waits.
 *
 * Used by: ListingsClient on /listings page
 */
import { MapBoundary } from '@/shared/components/map/MapBoundary';
import { AdvancedMarker, type MapCameraChangedEvent } from '@/shared/components/map/vis-gl';
import { useLocale, useTranslations } from '@/shared/i18n/useTranslations';
import type { ListingCardData, MapPositionData } from '@/shared/lib/types/listing-card';
import { autoUpdate, computePosition } from '@floating-ui/dom';
import { DEFAULT_MAP_VIEWPORT } from '@letsgethome/lib/constants';
import { displayPriceForIntent, formatPrice } from '@letsgethome/lib/currency';
import { isValidCoordinates } from '@letsgethome/lib/wasm';
import { type ListingId } from '@letsgethome/types/branded';
import { isMobileViewport, useIsMobile } from '@letsgethome/ui/hooks/use-mobile';
import { cn } from '@letsgethome/ui/lib/utils';
import { UnstyledButton } from '@letsgethome/ui/unstyled-button';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';
import { useCallback, useEffect, useState } from 'react';

import {
  MAP_PANE_TESTID,
  MAP_PIN_TESTID,
  MAP_POPOVER_UNPLACED,
  MAP_POPUP_TESTID,
  mapPinSelection,
  mapPopoverConfig,
  mapPopoverStyle,
  type MapPopoverStyle,
} from './map-popover';
import { sheetHiddenStripPx, shiftLatByPixels, visibleCentreShiftPx } from './map-visible-inset';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

export interface ListingsMapProps {
  /** Listings to display as markers */
  listings: ListingCardData[];
  /**
   * Intent of the surrounding search (rent vs buy) — the map renders inside
   * the intent-scoped /listings page, so the mode comes from the search
   * state, never from guessing at the result set.
   */
  isRent: boolean;
  /** Initial map position */
  initialPosition?: MapPositionData;
  /** Currently selected listing ID */
  selectedId?: ListingId | null;
  /** Selected listing for the preview-card popover (desktop only) */
  selectedListing?: ListingCardData | null;
  /** Callback when a marker is clicked */
  onMarkerClick?: (listing: ListingCardData) => void;
  /** Callback when map bounds change (for refetching) */
  onBoundsChange?: (bounds: { north: number; east: number; south: number; west: number }) => void;
  /** Callback when user starts interacting with map (drag/zoom) */
  onInteractionStart?: () => void;
  /** Additional classes */
  className?: string;
  /** Content to render inside the selected-listing preview card popover */
  infoWindowContent?: React.ReactNode;
  /**
   * The results sheet rests OVER this pane (the mobile canvas surface). The
   * map's centre of attention is then the centre of the pane's VISIBLE part —
   * see `map-visible-inset.ts` for the strip, the shift and the measurement.
   */
  sheetOverlaysMap?: boolean;
}

// Default Hong Kong viewport — the Rust geo SSOT's decision (canonical HK
// center at city zoom), shared with both mobile maps.
const DEFAULT_VIEWPORT = DEFAULT_MAP_VIEWPORT;
const DEFAULT_POSITION: MapPositionData = {
  centerLat: DEFAULT_VIEWPORT.lat,
  centerLng: DEFAULT_VIEWPORT.lng,
  zoom: DEFAULT_VIEWPORT.zoom,
};

// Check if position is renderable — coordinate validity ((0,0) unset
// sentinel, NaN, out-of-range) is the Rust SSOT's decision.
function isValidPosition(pos?: MapPositionData): pos is MapPositionData {
  return !!pos && isValidCoordinates(pos.centerLat, pos.centerLng);
}

// ----------------------------------------------------------------------------
// Price Pill Marker
// ----------------------------------------------------------------------------

interface PriceMarkerProps {
  listing: ListingCardData;
  isSelected: boolean;
  onClick: () => void;
  isRent: boolean;
  /**
   * Callback ref, supplied ONLY for the selected pill — this element is the
   * popover's reference, and the popover has to point at the pill the reader
   * actually clicked. Passing it conditionally is what keeps it fresh: React
   * detaches the previous pill's ref (calling it with `null`) before attaching
   * the new one, so a selection change cannot leave a stale box behind.
   */
  pillRef?: (el: HTMLButtonElement | null) => void;
}

function PriceMarker({ listing, isSelected, onClick, isRent, pillRef }: PriceMarkerProps) {
  const locale = useLocale();
  // STRICT intent-scoped price (Rust SSOT): undefined = the listing is not
  // offered for the searched intent or is unpriced — an upstream bug. Render
  // no price pill; NEVER substitute the other mode's price.
  const priceCents = displayPriceForIntent(
    isRent,
    listing.forRent,
    listing.forSale,
    listing.rentPriceCents,
    listing.salePriceCents
  );
  if (priceCents === undefined) {
    return null;
  }

  const formattedPrice = formatPrice(priceCents, locale);

  return (
    <UnstyledButton
      ref={pillRef}
      onClick={onClick}
      data-testid={MAP_PIN_TESTID}
      {...mapPinSelection(isSelected)}
      className={cn(
        'rounded-full px-3 py-1.5 text-sm font-semibold shadow-md transition-all',
        'hover:scale-105 hover:shadow-lg',
        isSelected
          ? 'bg-foreground text-background scale-110'
          : 'bg-background text-foreground hover:bg-accent'
      )}
    >
      {formattedPrice}
    </UnstyledButton>
  );
}

// ----------------------------------------------------------------------------
// Selected-listing popover (desktop)
// ----------------------------------------------------------------------------

interface SelectedListingPopoverProps {
  /** The map pane: the popover's offset parent AND its overflow boundary. */
  pane: HTMLElement | null;
  /** The selected price pill: the popover's reference element. NON-NULL. */
  pill: HTMLElement;
  children: React.ReactNode;
}

/**
 * The preview card, positioned by Floating UI relative to the selected pill and
 * kept inside the map pane.
 *
 * `autoUpdate(..., { animationFrame: true })` is what makes it follow a pan or
 * a zoom: the pill is an ordinary DOM node that Google moves, so watching its
 * BOX every frame needs no `google.maps` API at all — and the alternative
 * (recomputing from camera events) is the projected-point thinking this change
 * exists to remove.
 *
 * ★ THE ENGINE DIRECTLY, NOT THE REACT BINDING. `useFloating` would supply the
 * subscription and the style object below, and it types that style object as
 * `React.CSSProperties` from inside a third-party declaration file — which
 * resolved to an ERROR type in the sandbox `//web/apps/public:eslint` runs in
 * (see {@link MapPopoverStyle} for the measurement). `@floating-ui/dom` has no
 * React surface at all, and the plumbing it leaves to the caller is the effect
 * below. Owning fifteen lines is the cheaper half of that trade — and it means
 * the component and the unit test that proves its geometry now enter through
 * the SAME function, `computePosition`.
 */
function SelectedListingPopover({ pane, pill, children }: SelectedListingPopoverProps) {
  const [card, setCard] = useState<HTMLElement | null>(null);
  const [style, setStyle] = useState<MapPopoverStyle>(MAP_POPOVER_UNPLACED);

  useEffect(() => {
    if (!card) return;
    // `autoUpdate` calls this immediately and then on every frame the pill's
    // box could have moved, which on a Google map is every frame of a pan.
    const place = () => {
      void computePosition(pill, card, mapPopoverConfig(pane)).then(({ x, y }) => {
        setStyle(mapPopoverStyle(x, y));
      });
    };
    return autoUpdate(pill, card, place, { animationFrame: true });
  }, [pane, pill, card]);

  return (
    <div
      ref={setCard}
      style={style}
      data-testid={MAP_POPUP_TESTID}
      // The card's own layer, unchanged (page scope: above the pane, under the
      // results sheet). `w-80` matches SelectedListingCard's desktop width so
      // the box Floating UI measures is the box the reader sees.
      className={cn('w-80', Z_LAYERS.mapPreview.className)}
    >
      {children}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Map Content (needs map context)
// ----------------------------------------------------------------------------

interface MapContentProps {
  listings: ListingCardData[];
  isRent: boolean;
  selectedId?: ListingId | null;
  onMarkerClick?: (listing: ListingCardData) => void;
  /** Receives the selected pill's element (or `null` when nothing is selected). */
  onSelectedPill: (el: HTMLButtonElement | null) => void;
}

/**
 * The strip of `pane` the RESTING results sheet covers, in px — measured on
 * the client once the pane exists: the pane's bottom edge against the sheet's
 * top edge, the latter from the sheet's own SSOT (`map-visible-inset.ts`).
 * `null` until measured, `0` unless the sheet overlays this pane on a mobile
 * viewport — on `md+` the list sits BESIDE the map and covers none of it.
 *
 * ★ The viewport is read SYNCHRONOUSLY here (`isMobileViewport`), not through
 * `useIsMobile`: that hook hydrates with the server's answer (desktop) and
 * corrects itself a render later, and the map's `defaultCenter` is read ONCE,
 * at mount — so the map waits for THIS measurement (`null`) rather than mount
 * on the server's guess and never move. Measured once, deliberately: the
 * resting rail is the product's choice of what the page shows first, and a
 * later resize or drag is the reader's own.
 */
function useSheetHiddenStripPx(pane: HTMLElement | null, sheetOverlaysMap: boolean): number | null {
  const [strip, setStrip] = useState<number | null>(null);
  useEffect(() => {
    if (!pane) return;
    setStrip(
      sheetOverlaysMap && isMobileViewport()
        ? sheetHiddenStripPx(pane.getBoundingClientRect().bottom, window.innerHeight)
        : 0
    );
  }, [pane, sheetOverlaysMap]);
  return strip;
}

function MapContent({
  listings,
  isRent,
  selectedId,
  onMarkerClick,
  onSelectedPill,
}: MapContentProps) {
  return (
    <>
      {/* An absent `coordinate` means "no renderable position" (wire epoch 19
          — the old (0,0) sentinel is gone): such listings get no marker. */}
      {listings.map(
        (listing) =>
          listing.coordinate && (
            <AdvancedMarker
              key={listing.id}
              position={listing.coordinate}
              onClick={() => onMarkerClick?.(listing)}
            >
              <PriceMarker
                listing={listing}
                isSelected={selectedId === listing.id}
                onClick={() => onMarkerClick?.(listing)}
                isRent={isRent}
                pillRef={selectedId === listing.id ? onSelectedPill : undefined}
              />
            </AdvancedMarker>
          )
      )}
    </>
  );
}

// ----------------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------------

// eslint-disable-next-line @letsgethome/max-component-props, @letsgethome/no-passthrough-props -- map component API: props forward to MapContent which requires map context (AdvancedMarker must be inside <Map>)
export function ListingsMap({
  listings,
  isRent,
  initialPosition,
  selectedId,
  selectedListing,
  onMarkerClick,
  onBoundsChange,
  onInteractionStart,
  className,
  infoWindowContent,
  sheetOverlaysMap = false,
}: ListingsMapProps) {
  // Use default if position is undefined or invalid (0,0)
  const position = isValidPosition(initialPosition) ? initialPosition : DEFAULT_POSITION;

  const isMobile = useIsMobile();
  const tCommon = useTranslations('common');

  // The two elements the popover is a function of, both held as STATE rather
  // than refs: Floating UI has to recompute when either arrives, and a mutable
  // ref changes nothing about the render.
  const [pane, setPane] = useState<HTMLElement | null>(null);
  const [selectedPill, setSelectedPill] = useState<HTMLButtonElement | null>(null);
  // The centre of attention is the centre of the VISIBLE map: the point the
  // server centred moves DOWN by half the strip the resting sheet covers, so
  // it renders at the visible centre rather than under the drawer's handle
  // (`map-visible-inset.ts`). Pure Mercator at the initial zoom — no `panBy`,
  // whose animation a pin can be measured in the middle of.
  const hiddenStripPx = useSheetHiddenStripPx(pane, sheetOverlaysMap);
  const centre =
    hiddenStripPx === null
      ? null
      : {
          lat: shiftLatByPixels(
            position.centerLat,
            position.zoom,
            visibleCentreShiftPx(hiddenStripPx)
          ),
          lng: position.centerLng,
        };

  // A deliberate gesture (drag/zoom). `onInteractionStart` drives BOTH the
  // viewport-search gate and the mobile-sheet collapse-to-peek (via the hook's
  // interaction counter, SV-C4) — no global DOM event.
  const handleDragStart = useCallback(() => {
    onInteractionStart?.();
  }, [onInteractionStart]);

  return (
    // The map PANE: the popover's offset parent and the boundary `shift`/`flip`
    // hold it inside. `relative` is the whole of that contract, which is why it
    // is spelled here and not left to whatever wrapper a caller happens to use.
    <div ref={setPane} data-testid={MAP_PANE_TESTID} className={cn('relative', className)}>
      {/* The map mounts once the pane has been measured (`defer` until then):
          `defaultCenter` is read once, and the centre is a function of the
          measurement. Until both that and the boundary's in-view gate pass,
          the pane is the boundary's request-free placeholder — which is also
          what the server renders. */}
      <MapBoundary
        load="in-view"
        label={tCommon('a11y.map')}
        defer={centre === null}
        map={{
          defaultCenter: centre ?? { lat: position.centerLat, lng: position.centerLng },
          defaultZoom: position.zoom,
          gestureHandling: 'greedy',
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          onDragstart: handleDragStart,
          onZoomChanged: handleDragStart,
          onCameraChanged: (event: MapCameraChangedEvent) => {
            // `event.detail.bounds` resolves to `any` here (the google.maps
            // ambient types aren't visible to the type-checker), so this explicit
            // shape IS the typed access — there is no library type to use instead.
            const bounds = event.detail.bounds as
              | { north: number; east: number; south: number; west: number }
              | undefined;
            if (bounds && onBoundsChange) {
              onBoundsChange({
                north: bounds.north,
                east: bounds.east,
                south: bounds.south,
                west: bounds.west,
              });
            }
          },
        }}
      >
        <MapContent
          listings={listings}
          isRent={isRent}
          selectedId={selectedId}
          onMarkerClick={onMarkerClick}
          onSelectedPill={setSelectedPill}
        />
      </MapBoundary>

      {/* Desktop preview card. Mobile keeps its bottom sheet (ListingsClient) —
          a sheet has no reference element and needs none.

          The `selectedPill` guard is not defensive, it is the definition: the
          pill is absent exactly when the selected listing has no price for the
          searched intent and therefore rendered no marker content. A popover
          with nothing to point at is not a popover, and the card this replaces
          answered that case by planting itself at the raw coordinate. */}
      {!isMobile && selectedListing && infoWindowContent && selectedPill && (
        <SelectedListingPopover key={selectedListing.id} pane={pane} pill={selectedPill}>
          {infoWindowContent}
        </SelectedListingPopover>
      )}
    </div>
  );
}
